<template>
  <div class="stakeholder-list">
    <button-list
      :items="stakeholders"
      prop="title"
      :deletable="isIndependent"
      :removable="!isIndependent"
      :selected-items="[...selectedStakeholders]"
      :sortable="isIndependent"
      :order="[...stakeholderOrder]"
      @remove="removeStakeholder"
      @delete="deleteStakeholder"
      @select="selectStakeholder"
      @update:order="updateStakeholderOrder($event)"
      @contextmenu="onContextMenu($event)"
    >
      <template #after-list>
        <input-text-create
          v-if="isIndependent"
          @submit="addNew(null, { title: $event })"
        />
        <!-- <button
          v-if="isIndependent"
          key="stakeholderlist-add"
          type="button"
          class="button as-start"
          @click="addNew()"
        >
          {{ $t("labels.add") }}
        </button> -->
        <input-dropdown
          v-if="!isIndependent"
          key="stakeholderlist-add"
          :options="otherStakeholders"
          :order="[...stakeholderOrder, newStakeholderKey]"
          :clear-on-select="true"
          prop="title"
          @select="addNew($event.item)"
        />
      </template>
    </button-list>

    <context-menu
      v-if="contextMenuData.stakeholder"
      :visible="contextMenuData.stakeholder != null"
      :x="contextMenuData.x"
      :y="contextMenuData.y"
      @close="contextMenuData.stakeholder = null"
    >
      <button
        v-if="isIndependent"
        type="button"
        class="context-menu__entry context-menu__entry--danger"
        @click="onContextMenuDelete(contextMenuData.stakeholder)"
      >
        <lucide-icon name="trash-2" class="align-middle" />
        <span>{{ $t("actions.deleteStakeholder") }}</span>
      </button>
      <button
        v-if="!isIndependent"
        type="button"
        class="context-menu__entry context-menu__entry"
        @click="onContextMenuRemove(contextMenuData.stakeholder)"
      >
        <span>{{ $t("actions.removeStakeholder") }}</span>
      </button>
    </context-menu>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed } from "vue";
import { pickBy, filter, includes } from "lodash-es";
import { useI18n } from "vue-i18n";

import LucideIcon from "@core/ui/LucideIcon.vue";
import ButtonList from "@core/ui/ButtonList.vue";
import InputDropdown from "@core/input/InputDropdown.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";
import InputTextCreate from "@core/input/InputTextCreate.vue";

import { showError } from "@core/ui/alert/alert";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useStakeholderStore } from "@editor/items/stakeholder/stakeholders.store";

import type { StakeholderItem } from "./StakeholderItem";
import type { Uid } from "@/helpers/utility/Id";

interface Props {
  itemType?: "resource" | "target" | "action" | "index";
  itemId?: number;
  selectEnabled?: boolean;
  selectedStakeholderIds?: number[] | undefined;
}
const props = withDefaults(defineProps<Props>(), {
  itemType: undefined,
  itemId: undefined,
  selectEnabled: true
});

const emit = defineEmits<{
  (e: "select", item: { uid: Uid<"stakeholder"> }): void;
  (e: "deselect", item: { uid: Uid<"stakeholder"> }): void;
}>();

const { t } = useI18n();

const stakeholderStore = useStakeholderStore();
const selectionStore = useSelectionStore();

const contextMenuData = reactive({
  visible: false,
  x: 0,
  y: 0,
  stakeholder: null as null | StakeholderItem
});

const isIndependent = computed(() => {
  return props.itemType == null || props.itemId == null;
});

const allStakeholders = computed(() => {
  return useStakeholderStore().items;
});

const newStakeholderKey = -1;

const stakeholders = computed(() => {
  const itemType = props.itemType;
  const itemId = props.itemId;
  if (itemType != null && itemId != null) {
    return pickBy(allStakeholders.value, (stakeholder) => stakeholder[`${itemType}Ids`].includes(itemId));
  }

  // all
  return allStakeholders.value;
});

const otherStakeholders = computed(() => {
  const list: (StakeholderItem | { id: -1; title: string })[] = filter(
    allStakeholders.value,
    (stakeholder) => includes(stakeholders.value, stakeholder) == false
  );

  list.push({ title: "+ " + t("labels.new"), id: newStakeholderKey });

  return list;
});

const selectedStakeholders = computed(() => {
  let ids = props.selectedStakeholderIds;
  if (ids == null) {
    ids = props.selectEnabled ? selectionStore.selectedStakeholderIds : [];
  }
  return useStakeholderStore().getArrayByIds(ids);
});

const stakeholderOrder = computed(() => {
  return useStakeholderStore().order;
});

/**
 * @param stakeholder the stakeholder to add
 */
async function addNew(
  stakeholder: null | { id: number; type: "stakeholder"; uid: Uid<"stakeholder"> } | { id: -1 },
  startData: Partial<StakeholderItem> = {}
) {
  // create totally new stakeholder
  if (stakeholder == null || stakeholder.id == -1 || !("uid" in stakeholder)) {
    const targetIds = [];
    const actionIds = [];
    const resourceIds = [];
    const indexIds = [];
    if (props.itemType == "target") targetIds.push(props.itemId);
    if (props.itemType == "action") actionIds.push(props.itemId);
    if (props.itemType == "resource") resourceIds.push(props.itemId);
    if (props.itemType == "index") indexIds.push(props.itemId);

    const newStakeholder = await stakeholderStore.create({
      ...startData,
      targetIds,
      actionIds,
      resourceIds,
      indexIds
    });
    return selectStakeholder(newStakeholder);
  }

  const itemId = props.itemId;
  if (itemId != null) {
    // add stakeholder to object
    if (props.itemType == "target") {
      stakeholderStore.addTarget({
        id: stakeholder.id,
        targetId: itemId
      });
    } else if (props.itemType == "action") {
      stakeholderStore.addAction({
        id: stakeholder.id,
        actionId: itemId
      });
    } else if (props.itemType == "resource") {
      stakeholderStore.addResource({
        id: stakeholder.id,
        resourceId: itemId
      });
    } else if (props.itemType == "index") {
      stakeholderStore.addIndex({
        id: stakeholder.id,
        indexId: itemId
      });
    } else {
      showError(t("errors.stakeholderExistsAlready"));
    }
  }

  selectStakeholder({ type: "stakeholder", id: stakeholder.id, uid: stakeholder.uid });
}
function removeStakeholder(stakeholder: { id: number; type: "stakeholder"; uid: Uid<"stakeholder"> }) {
  deselect(stakeholder);

  if (props.itemId != null) {
    if (props.itemType == "target") {
      stakeholderStore.removeTarget({
        id: stakeholder.id,
        targetId: props.itemId
      });
    } else if (props.itemType == "action") {
      stakeholderStore.removeAction({
        id: stakeholder.id,
        actionId: props.itemId
      });
    } else if (props.itemType == "resource") {
      stakeholderStore.removeResource({
        id: stakeholder.id,
        resourceId: props.itemId
      });
    } else if (props.itemType == "index") {
      stakeholderStore.removeIndex({
        id: stakeholder.id,
        indexId: props.itemId
      });
    }
  }

  emit("deselect", { uid: stakeholder.uid });
}
function deleteStakeholder(stakeholder: { id: number; type: "stakeholder"; uid: Uid<"stakeholder"> }) {
  deselect(stakeholder);

  stakeholderStore.delete({
    id: stakeholder.id
  });
}
function selectStakeholder(item: { id: number; type: "stakeholder"; uid: Uid<"stakeholder"> }) {
  // check if the stakeholder is currently selected
  const isSelected = selectedStakeholders.value.some((stakeholder) => stakeholder.id == item.id);

  // deselect all stakeholders
  if (props.selectEnabled) selectionStore.deselectStakeholders();

  // select it if it wasn't selected to begin with
  if (!isSelected) {
    if (props.selectEnabled) selectionStore.addStakeholder(item);
    emit("select", { uid: item.uid });
  } else {
    deselect(item);
  }
}
function deselect(stakeholder: { id: number; type: "stakeholder"; uid: Uid<"stakeholder"> }) {
  if (props.selectEnabled) selectionStore.removeStakeholder(stakeholder);
  emit("deselect", { uid: stakeholder.uid });
}
function updateStakeholderOrder(order: number[]) {
  stakeholderStore.saveOrder(order);
}

function onContextMenu(event: MouseEvent & { item: StakeholderItem | null }) {
  contextMenuData.x = event.clientX;
  contextMenuData.y = event.clientY;
  contextMenuData.stakeholder = event.item;
}
function onContextMenuRemove(stakeholder: { id: number; type: "stakeholder"; uid: Uid<"stakeholder"> }) {
  removeStakeholder(stakeholder);
  contextMenuData.stakeholder = null;
}
function onContextMenuDelete(stakeholder: { id: number; type: "stakeholder"; uid: Uid<"stakeholder"> }) {
  deleteStakeholder(stakeholder);
  contextMenuData.stakeholder = null;
}
</script>

<style lang="scss">
.stakeholder-list {
  min-height: 2rem;

  &__entry {
    position: relative;
    display: flex;
  }

  &__doc-anchor {
    padding-left: 0.5em;
  }

  &__input {
    width: 100%;
    position: relative;
  }
}
</style>
